import React, { useState, useMemo } from 'react';
import { useApp } from '../store';
import { UserRole, ProjectStage } from '../types';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Cell, 
  Legend,
  ComposedChart,
  Line
} from 'recharts';
import { Trophy, Target, TrendingUp, CheckCircle, Award, Paintbrush, Code, Users, Star, Calendar, Filter } from 'lucide-react';
// Fix: remove parseISO and subMonths as they are not available from the installed date-fns version
import { isSameMonth } from 'date-fns';

const Leaderboard = () => {
  const { getDevRankings, users, projects } = useApp();
  const [activeTab, setActiveTab] = useState<'dev' | 'designer'>('dev');
  const [monthFilter, setMonthFilter] = useState<'current' | 'last'>('current');
  
  const targetDate = useMemo(() => {
    // Fix: Replace subMonths with native Date logic
    if (monthFilter === 'current') return new Date();
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  }, [monthFilter]);

  const rankings = getDevRankings(targetDate);

  // Designer Performance Aggregation
  const designerRankings = useMemo(() => {
    const designers = users.filter(u => u.role === UserRole.DESIGNER);
    return designers.map(designer => {
      const designerProjects = projects.filter(p => p.assignedDesignerId === designer.id && p.designQualityRating !== undefined);
      const monthlyProjects = designerProjects.filter(p => {
        // Fix: Replace parseISO with native Date constructor
        const date = p.completedAt ? new Date(p.completedAt) : new Date(p.createdAt);
        return isSameMonth(date, targetDate);
      });

      const avgQuality = designerProjects.length > 0 ? designerProjects.reduce((acc, p) => acc + (p.designQualityRating || 0), 0) / designerProjects.length : 0;
      const avgQA = designerProjects.length > 0 ? designerProjects.reduce((acc, p) => acc + (p.designContentQARating || 0), 0) / designerProjects.length : 0;
      const avgSpeed = designerProjects.length > 0 ? designerProjects.reduce((acc, p) => acc + (p.designDeadlineRating || 0), 0) / designerProjects.length : 0;
      
      const completedCount = projects.filter(p => 
        // Fix: Replace parseISO with native Date constructor
        p.assignedDesignerId === designer.id && p.stage === ProjectStage.COMPLETED && p.completedAt && isSameMonth(new Date(p.completedAt), targetDate)
      ).length;

      const monthlyTotalScore = monthlyProjects.reduce((acc, p) => {
        return acc + (p.designQualityRating || 0) + (p.designContentQARating || 0) + (p.designDeadlineRating || 0);
      }, 0);

      return {
        userId: designer.id,
        userName: designer.name,
        avatar: designer.avatar,
        avgQuality,
        avgQA,
        avgSpeed,
        completedCount,
        totalScore: monthlyTotalScore
      };
    }).sort((a, b) => b.totalScore - a.totalScore);
  }, [users, projects, targetDate]);

  const devChartData = rankings.map(r => ({
    name: r.userName,
    QA: 100 - r.qaFailureRate,
    Delivery: r.onTimeDeliveryRate,
    Completed: r.completedProjects,
    Points: r.monthlyPoints
  }));

  const designerChartData = designerRankings.map(r => ({
    name: r.userName,
    Quality: r.avgQuality,
    QA: r.avgQA,
    Speed: r.avgSpeed,
    Score: r.totalScore
  }));

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase italic">Nice DIgital Leader Board</h2>
          <div className="flex items-center gap-3 mt-1">
             <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px]">Cross-departmental performance metrics</p>
             <span className="text-[10px] font-black text-indigo-500 italic bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-tighter">
               Note: Performance Matrix calculated monthly
             </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
            <button 
              onClick={() => setMonthFilter('current')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${monthFilter === 'current' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Calendar className="w-3.5 h-3.5" />
              This Month
            </button>
            <button 
              onClick={() => setMonthFilter('last')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${monthFilter === 'last' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Filter className="w-3.5 h-3.5" />
              Last Month
            </button>
          </div>

          <div className="bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm flex gap-2">
            <button 
              onClick={() => setActiveTab('dev')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'dev' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'}`}
            >
              <Code className="w-4 h-4" />
              Dev Managers
            </button>
            <button 
              onClick={() => setActiveTab('designer')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'designer' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'}`}
            >
              <Paintbrush className="w-4 h-4" />
              Designers
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'dev' ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic mb-8">Dev Quality Analytics</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={devChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <YAxis fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} />
                    <Legend verticalAlign="top" align="right" height={36} iconType="circle" wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em' }} />
                    <Bar dataKey="QA" name="QA Pass Rate %" fill="#10b981" radius={[10, 10, 0, 0]} barSize={40} />
                    <Line type="monotone" dataKey="Delivery" name="On-Time %" stroke="#6366f1" strokeWidth={4} dot={{ r: 6, fill: '#6366f1' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic mb-8">Completed Projects</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={devChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <YAxis fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="Completed" fill="#818cf8" radius={[10, 10, 0, 0]} barSize={50}>
                      {devChartData.map((_, index) => <Cell key={`cell-${index}`} fill={index === 0 ? '#6366f1' : '#e2e8f0'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic">{monthFilter === 'current' ? 'This Month Dev Performance' : 'Historical Performance Matrix'}</h3>
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-slate-100">
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Manager</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">QA Efficiency</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Completed Project</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Performance Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rankings.map((rank, i) => (
                  <tr key={rank.userId} className="hover:bg-slate-50 transition-all group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <span className={`w-6 text-xs font-black ${i === 0 ? 'text-amber-500' : 'text-slate-300'}`}>#{i+1}</span>
                        <img src={users.find(u => u.id === rank.userId)?.avatar} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="" />
                        <p className="font-black text-slate-900 tracking-tight text-lg italic">{rank.userName}</p>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                       <span className="text-[10px] font-black text-slate-500">{Math.round(100-rank.qaFailureRate)}% Pass Rate</span>
                    </td>
                    <td className="px-8 py-5 text-center">
                       <span className="px-4 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">{rank.completedProjects} units</span>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <span className="px-6 py-2 bg-indigo-600 text-white rounded-2xl text-lg font-black italic shadow-lg shadow-indigo-100">{rank.monthlyPoints}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic mb-8">Design Skillset Matrix</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={designerChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" domain={[0, 10]} hide />
                    <YAxis dataKey="name" type="category" fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <Tooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="Quality" name="Quality" fill="#6366f1" radius={[0, 5, 5, 0]} barSize={20} />
                    <Bar dataKey="QA" name="QA Integrity" fill="#10b981" radius={[0, 5, 5, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic mb-8">Monthly Aggregate Scores</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={designerChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 'auto']} fontSize={10} fontWeight="black" axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="Score" fill="#818cf8" radius={[10, 10, 0, 0]} barSize={50}>
                      {designerChartData.map((_, index) => <Cell key={`cell-${index}`} fill={index === 0 ? '#6366f1' : '#e2e8f0'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight italic">Designer Performance Matrix</h3>
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-slate-100">
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Lead Designer</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Quality Avg.</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Completed Project</th>
                  <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Performance Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {designerRankings.map((rank, i) => (
                  <tr key={rank.userId} className="hover:bg-slate-50 transition-all group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <span className={`w-6 text-xs font-black ${i === 0 ? 'text-amber-500' : 'text-slate-300'}`}>#{i+1}</span>
                        <img src={rank.avatar} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" alt="" />
                        <p className="font-black text-slate-900 tracking-tight text-lg italic">{rank.userName}</p>
                      </div>
                    </td>
                    <td className="px-8 py-5 text-center">
                       <span className="text-[10px] font-black text-slate-500">{rank.avgQuality.toFixed(1)}/10 Avg</span>
                    </td>
                    <td className="px-8 py-5 text-center">
                       <span className="px-4 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">{rank.completedCount} units</span>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <span className="px-6 py-2 bg-indigo-600 text-white rounded-2xl text-lg font-black italic shadow-lg shadow-indigo-100">{rank.totalScore}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Leaderboard;